import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["items", "next", "sentinel", "pagination"];

  connect() {
    this.loading = false;

    if (this.hasPaginationTarget) this.paginationTarget.style.display = "none";

    if (!this.hasSentinelTarget) return;
    if (!this.hasNextTarget || !this.nextTarget?.href) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.loadNext();
      },
      {
        root: null,
        rootMargin: "700px 0px",
        threshold: 0.01,
      }
    );

    this.observer.observe(this.sentinelTarget);
  }

  disconnect() {
    try {
      this.observer?.disconnect();
    } catch (_) {}
  }

  async loadNext() {
    if (this.loading) return;
    if (!this.hasItemsTarget) return;
    if (!this.hasNextTarget || !this.nextTarget?.href) return;

    this.loading = true;

    const nextUrl = this.nextTarget.href;

    const prevHTML = this.sentinelTarget.innerHTML;
    this.sentinelTarget.classList.add("is-loading");
    this.sentinelTarget.innerHTML = this.loadingHTML();

    try {
      const resp = await fetch(nextUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      });
      if (!resp.ok) throw new Error(`Failed to load next page (${resp.status})`);

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const newItems = doc.querySelector('[data-infinite-scroll-target="items"]');
      if (!newItems) {
        this.stop();
        return;
      }

      const fragment = document.createDocumentFragment();
      Array.from(newItems.children).forEach((child) => fragment.appendChild(child));
      this.itemsTarget.appendChild(fragment);

      const newNext = doc.querySelector('[data-infinite-scroll-target="next"]');
      const newHref = newNext?.getAttribute("href");
      if (newHref) {
        this.nextTarget.setAttribute("href", newHref);
      } else {
        this.stop();
      }

      const revealController = this.application.getControllerForElementAndIdentifier(document.body, "scroll-reveal")
      if (revealController) {
        document.querySelectorAll("[data-reveal]:not(.revealed)").forEach((el) => {
          revealController.observer?.observe(el)
        })
      }
    } catch (e) {
      console.error(e);
      if (this.hasPaginationTarget) this.paginationTarget.style.display = "";
      this.stop();
    } finally {
      this.sentinelTarget.classList.remove("is-loading");
      this.sentinelTarget.innerHTML = prevHTML;
      this.loading = false;
    }
  }

  loadingHTML() {
    if (this.itemsTarget?.tagName === "TBODY") {
      return (
        '<div class="infinite-scroll-skeleton" style="grid-template-columns:1fr;margin:0 auto;">' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '<div class="infinite-scroll-skeleton__row" style="height:16px;"></div>' +
        '</div>'
      );
    }

    const shimmer =
      "background:linear-gradient(90deg,rgba(0,13,68,0.06) 25%,rgba(0,13,68,0.12) 37%,rgba(0,13,68,0.06) 63%);background-size:800px 100%;";
    return (
      '<div style="display:flex;flex-direction:column;gap:14px;">' +
      '<div class="infinite-scroll-skeleton__card" style="height:96px;border-radius:14px;' + shimmer + '"></div>' +
      '</div>'
    );
  }

  stop() {
    try {
      if (this.observer && this.hasSentinelTarget) this.observer.unobserve(this.sentinelTarget);
      this.observer?.disconnect();
    } catch (_) {}

    try {
      this.sentinelTarget?.remove();
    } catch (_) {}

    try {
      this.nextTarget?.removeAttribute("href");
    } catch (_) {}
  }
}
